
import React, { useEffect, useRef, useState } from 'react';

const Invaders: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext('2d')!;
    
    // HD Setup
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);
    const cw = rect.width, ch = rect.height;

    let playerX = cw / 2;
    let bullets: { x: number, y: number }[] = [];
    let invaders: { x: number, y: number, color: string }[] = [];
    let invDirection = 1;
    let invSpeed = 1;

    for (let i = 0; i < 5; i++) {
      for (let j = 0; j < 8; j++) {
        invaders.push({ 
          x: j * 50 + 50, 
          y: i * 40 + 50, 
          color: i < 2 ? '#ef4444' : (i < 4 ? '#f59e0b' : '#3b82f6')
        });
      }
    }

    const loop = () => {
      if (gameOver) return;
      ctx.clearRect(0, 0, cw, ch);
      
      // Player (Ship)
      ctx.fillStyle = '#10b981';
      ctx.shadowBlur = 15;
      ctx.shadowColor = '#10b981';
      ctx.beginPath();
      ctx.moveTo(playerX, ch - 40);
      ctx.lineTo(playerX - 20, ch - 15);
      ctx.lineTo(playerX + 20, ch - 15);
      ctx.fill();

      // Bullets (Laser beams)
      ctx.shadowBlur = 10;
      ctx.shadowColor = '#fff';
      ctx.fillStyle = '#fff';
      bullets.forEach((b, bi) => {
        b.y -= 7;
        ctx.fillRect(b.x - 1, b.y, 2, 12);
        
        invaders.forEach((inv, ii) => {
          if (b.x > inv.x && b.x < inv.x + 35 && b.y > inv.y && b.y < inv.y + 25) {
            invaders.splice(ii, 1);
            bullets.splice(bi, 1);
            setScore(s => s + 100);
          }
        });
      });

      // Invaders (HD shapes)
      invaders.forEach(inv => {
        inv.x += invDirection * invSpeed;
        ctx.shadowBlur = 10;
        ctx.shadowColor = inv.color;
        ctx.fillStyle = inv.color;
        ctx.beginPath();
        ctx.roundRect(inv.x, inv.y, 35, 25, 4);
        ctx.fill();
        
        if (inv.y > ch - 60) setGameOver(true);
      });

      if (invaders.length > 0) {
        const left = Math.min(...invaders.map(i => i.x));
        const right = Math.max(...invaders.map(i => i.x));
        if (left < 20 || right > cw - 55) {
          invDirection *= -1;
          invSpeed += 0.1;
          invaders.forEach(inv => inv.y += 15);
        }
      } else {
        // Respawn logic
        setGameOver(true); // Treat win as restartable game over for now
      }

      ctx.shadowBlur = 0;
      requestAnimationFrame(loop);
    };

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') playerX = Math.max(20, playerX - 25);
      if (e.key === 'ArrowRight') playerX = Math.min(cw - 20, playerX + 25);
      if (e.key === ' ') {
        if (bullets.length < 3) bullets.push({ x: playerX, y: ch - 45 });
      }
    };
    
    window.addEventListener('keydown', handleKey);
    const animId = requestAnimationFrame(loop);
    return () => {
      window.removeEventListener('keydown', handleKey);
      cancelAnimationFrame(animId);
    };
  }, [gameOver]);

  return (
    <div className="flex flex-col items-center game-container">
      <div className="mb-6 glass px-8 py-3 rounded-2xl flex justify-between w-full max-w-lg items-center">
        <span className="text-xs font-black text-emerald-400 tracking-widest uppercase">Firewall Integrity: Online</span>
        <span className="text-2xl font-black font-mono">SC:{score.toLocaleString()}</span>
      </div>
      
      <div className="relative group">
        <div className="absolute -inset-0.5 bg-gradient-to-b from-emerald-500/20 to-transparent rounded-2xl blur-xl"></div>
        <canvas 
          ref={canvasRef} 
          style={{ width: 'min(90vw, 600px)', height: '400px' }} 
          className="relative bg-slate-950 border border-slate-800 rounded-2xl shadow-2xl" 
        />
      </div>

      {gameOver && (
        <div className="mt-8 text-center animate-bounce">
          <p className="text-red-500 font-black text-2xl mb-4 tracking-tighter uppercase">Breach Detected</p>
          <button onClick={() => setGameOver(false)} className="bg-emerald-600 px-8 py-3 rounded-xl font-bold">RE-DEPLOY</button>
        </div>
      )}
      
      <p className="mt-6 text-[10px] text-slate-500 font-mono tracking-[0.3em] uppercase">Use ARROWS and SPACE to defend the stack</p>
    </div>
  );
};

export default Invaders;
